'use client';

import { useId, useState } from 'react';

import { testAnchorJoin } from '../../lib/testAnchors';

/** A choice-only combobox: typed text filters; only picking an option answers. */
export function SearchableSelectInput({
  options,
  value,
  onChange,
  labelledById,
  fieldKey,
}: {
  options: { value: string; label: string }[];
  value: unknown;
  onChange: (next: unknown) => void;
  labelledById?: string;
  fieldKey?: string;
}) {
  const id = useId();
  const [query, setQuery] = useState<string | null>(null);
  const [open, setOpen] = useState(false);
  const [activeIndex, setActiveIndex] = useState(0);
  const selected = options.find((option) => option.value === value);
  const search = (query ?? '').trim().toLocaleLowerCase();
  const matches = options.filter((option) =>
    option.label.toLocaleLowerCase().includes(search) ||
    option.value.toLocaleLowerCase().startsWith(search),
  );
  const active = matches[activeIndex];
  const optionId = (optionValue: string) => `${id}-${optionValue}`;

  function choose(option: { value: string; label: string }) {
    setQuery(null);
    setOpen(false);
    onChange(option.value);
  }

  return (
    <div
      className="relative w-full"
      onBlur={(event) => {
        if (!event.currentTarget.contains(event.relatedTarget)) {
          setOpen(false);
          setQuery(null);
        }
      }}
    >
      <div className="relative">
        <input
          role="combobox"
          aria-labelledby={labelledById}
          aria-label={labelledById ? undefined : 'Choose an option'}
          aria-autocomplete="list"
          aria-expanded={open}
          aria-controls={open ? `${id}-options` : undefined}
          aria-activedescendant={open && active ? optionId(active.value) : undefined}
          autoComplete="off"
          placeholder="Start typing to find an option"
          value={query ?? selected?.label ?? ''}
          className="min-h-[56px] w-full rounded-[var(--radius-md)] border border-[rgba(26,35,50,0.18)] bg-bg-card px-5 py-4 pr-12 font-body text-[16px] text-text-primary focus:outline-none focus-visible:ring-2 focus-visible:ring-accent/40"
          onFocus={() => { setOpen(true); setActiveIndex(0); }}
          onChange={(event) => {
            setQuery(event.target.value);
            setOpen(true);
            setActiveIndex(0);
            if (value !== undefined && value !== null) onChange(undefined);
          }}
          onKeyDown={(event) => {
            if (event.key === 'ArrowDown' || event.key === 'ArrowUp') {
              event.preventDefault();
              setOpen(true);
              const next = open
                ? Math.max(0, Math.min(matches.length - 1, activeIndex + (event.key === 'ArrowDown' ? 1 : -1)))
                : 0;
              setActiveIndex(next);
              requestAnimationFrame(() => {
                const option = matches[next];
                if (option) document.getElementById(optionId(option.value))?.scrollIntoView({ block: 'nearest' });
              });
            } else if (event.key === 'Enter' && open) {
              event.preventDefault();
              if (active) choose(active);
            } else if (event.key === 'Escape') {
              event.preventDefault();
              setOpen(false);
              setQuery(null);
            }
          }}
        />
        <span aria-hidden="true" className="pointer-events-none absolute right-5 top-1/2 -translate-y-1/2 text-text-secondary">⌄</span>
      </div>
      {open && (
        <div
          id={`${id}-options`}
          role="listbox"
          aria-labelledby={labelledById}
          aria-label={labelledById ? undefined : 'Options'}
          className="absolute z-20 mt-2 max-h-64 w-full overflow-y-auto rounded-[var(--radius-md)] border border-[rgba(26,35,50,0.18)] bg-bg-card p-1 shadow-lg"
        >
          {matches.map((option, index) => (
            <button
              key={option.value}
              id={optionId(option.value)}
              type="button"
              role="option"
              tabIndex={-1}
              aria-selected={value === option.value}
              data-testid={fieldKey ? `option-${testAnchorJoin(fieldKey, option.value)}` : `option-${option.value}`}
              className={`min-h-[44px] w-full rounded-[var(--radius-sm)] px-4 py-3 text-left font-body text-[16px] text-text-primary hover:bg-accent-light ${activeIndex === index ? 'bg-accent-light' : ''}`}
              onMouseDown={(event) => event.preventDefault()}
              onMouseEnter={() => setActiveIndex(index)}
              onClick={() => choose(option)}
            >
              {option.label}
            </button>
          ))}
          {matches.length === 0 && <p role="status" className="px-4 py-3 text-text-secondary">No matching options. Try another name.</p>}
        </div>
      )}
    </div>
  );
}
